import { useState } from "react";
import { Banner, Button, Field, Input, KeyValues, Modal, StatusDot, useToast, type DotTone } from "@/shared/rd";
import { SectionCard } from "@/shared/stores/components/rd-local";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type {
  OnlinePaymentProvider,
  OnlinePaymentStatus,
} from "@/lib/api/types";
import { formatCurrency } from "@/lib/formatting/currency";
import { useStoreOrderPaymentTransactionQuery } from "@/modules/stores/services/queries/useStoreOrderPaymentTransactionQuery";
import { useRefundStoreOrderMutation } from "@/modules/stores/services/mutations/useRefundStoreOrderMutation";

// Gateway brand names are proper nouns (same in en/ar), so they're not i18n keys.
const PROVIDER_LABEL: Record<OnlinePaymentProvider, string> = {
  paymob: "Paymob",
  paytabs: "PayTabs",
  kashier: "Kashier",
  fawry: "Fawry",
  stripe: "Stripe",
};

const STATUS_KEY: Record<OnlinePaymentStatus, string> = {
  initiated: keys.orders.onlinePayStatusInitiated,
  pending: keys.orders.onlinePayStatusPending,
  paid: keys.orders.onlinePayStatusPaid,
  failed: keys.orders.onlinePayStatusFailed,
  refunded: keys.orders.onlinePayStatusRefunded,
  cancelled: keys.orders.onlinePayStatusCancelled,
  expired: keys.orders.onlinePayStatusExpired,
};

const STATUS_TONE: Record<OnlinePaymentStatus, DotTone> = {
  paid: "green",
  refunded: "grey",
  failed: "red",
  cancelled: "grey",
  expired: "grey",
  pending: "amber",
  initiated: "amber",
};

type OnlinePaymentPanelProps = {
  storeId: string;
  orderId: string;
  locale: string;
};

// Admin order-detail card: how an online order was paid + a refund action
// (full or partial). Renders nothing for COD/manual orders.
export function OnlinePaymentPanel({
  storeId,
  orderId,
  locale,
}: OnlinePaymentPanelProps) {
  const t = useTranslations();
  const toast = useToast();
  const [refundFailed, setRefundFailed] = useState(false);
  const { transaction } = useStoreOrderPaymentTransactionQuery({
    storeId,
    orderId,
  });
  const refund = useRefundStoreOrderMutation();
  const [amount, setAmount] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!transaction) return null;

  const tone = STATUS_TONE[transaction.status];
  const canRefund = transaction.status === "paid";
  const maxAmount = transaction.amount;
  const providerLabel = PROVIDER_LABEL[transaction.provider];
  const fullLabel = formatCurrency(maxAmount, locale, transaction.currency);

  const trimmed = amount.trim();
  const parsedAmount = trimmed === "" ? maxAmount : Number(amount);
  // Empty input = full refund; otherwise 0 < amount ≤ paid.
  const isAmountValid =
    trimmed === "" ||
    (Number.isFinite(parsedAmount) &&
      parsedAmount > 0 &&
      parsedAmount <= maxAmount);

  const submitRefund = async () => {
    if (!isAmountValid) return;
    setRefundFailed(false);
    try {
      await refund.mutateAsync({
        storeId,
        orderId,
        ...(trimmed === "" ? {} : { amount: parsedAmount }),
      });
      toast.show({
        message: t("orders.refundedToast", {
          amount: formatCurrency(parsedAmount, locale, transaction.currency),
          provider: providerLabel,
        }),
      });
      setConfirmOpen(false);
      setAmount("");
    } catch {
      setRefundFailed(true);
      setConfirmOpen(false);
    }
  };

  return (
    <SectionCard
      badge="currency"
      title={t(keys.orders.onlinePaymentTitle)}
      aside={<StatusDot tone={tone}>{t(STATUS_KEY[transaction.status])}</StatusDot>}
    >
      {refundFailed ? (
        <Banner
          tone="error"
          title={t("orders.refundFailedTitle")}
          actions={
            <Button size="sm" loading={refund.isPending} loadingLabel={t("orders.refunding")} onClick={() => void submitRefund()}>
              {t(keys.shell.retry)}
            </Button>
          }
        >
          {t("orders.refundFailedText")}
        </Banner>
      ) : null}
      <KeyValues
        rows={[
          { label: t(keys.orders.onlinePayProvider), value: <span style={{ fontWeight: 500 }}>{providerLabel}</span> },
          {
            label: t(keys.orders.onlinePayAmount),
            value: (
              <span className="rd-num" style={{ fontWeight: 600 }}>
                {fullLabel}
              </span>
            ),
          },
          ...(transaction.providerTxnId
            ? [
                {
                  label: t(keys.orders.onlinePayReference),
                  value: (
                    <span dir="ltr" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                      {transaction.providerTxnId}
                    </span>
                  ),
                },
              ]
            : []),
        ]}
      />

      {canRefund ? (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 12 }}>
          <Field
            label={t(keys.orders.refundAmountLabel)}
            htmlFor={`refund-${orderId}`}
            error={isAmountValid ? undefined : t("orders.refundAmountInvalid", { max: fullLabel })}
          >
            <Input
              id={`refund-${orderId}`}
              type="number"
              name="refundAmount"
              min={0}
              step="0.01"
              placeholder={fullLabel}
              value={amount}
              invalid={!isAmountValid}
              onChange={(event) => setAmount(event.target.value)}
              style={{ width: 200 }}
            />
          </Field>
          <Button
            variant="danger"
            disabled={!isAmountValid || refund.isPending}
            onClick={() => setConfirmOpen(true)}
          >
            {t(keys.orders.refundAction)}
          </Button>
        </div>
      ) : null}


      <Modal
        open={confirmOpen}
        onClose={refund.isPending ? undefined : () => setConfirmOpen(false)}
        title={t(keys.orders.refundConfirmTitle)}
        actions={
          <>
            <Button muted variant="ghost" disabled={refund.isPending} onClick={() => setConfirmOpen(false)}>
              {t(keys.common.back)}
            </Button>
            <Button variant="danger" loading={refund.isPending} loadingLabel={t("orders.refunding")} onClick={() => void submitRefund()}>
              {t(keys.orders.refundAction)}
            </Button>
          </>
        }
      >
        <p className="rd-modal-text" style={{ margin: 0 }}>
          {t(keys.orders.refundConfirmBody, {
            amount: formatCurrency(parsedAmount, locale, transaction.currency),
            provider: providerLabel,
          })}
        </p>
      </Modal>
    </SectionCard>
  );
}
